import { gql, useQuery } from "@apollo/client";
import { useFocusEffect } from "@react-navigation/native";
import { useEffect, useCallback } from "react";
import { Platform } from "react-native";

import { CURRENT_MEMBER_FRAGMENT, CurrentMember } from "../../screens/fragments";
import client, { Client, MetaData } from "./client";
import type { Content } from "./types";
import { isPublicPath } from "../../screens/NavigatorScreen/utils";

export type { Client, Content };

export const CURRENT_MEMBER_INTERCOM = gql`
  query intercomQuery {
    currentMember {
      ...CurrentMemberFragment
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
`;

interface Query {
  currentMember: CurrentMember;
}

export function useIntercomClient({ skipUser = false }: { skipUser?: boolean } = {}) {
  const { data } = useQuery<Query>(CURRENT_MEMBER_INTERCOM, { fetchPolicy: "cache-first", skip: skipUser || isPublicPath() });
  const { externalId, intercomHmac, firstName, lastName, primaryEmail } = data?.currentMember || {};

  const hmac = intercomHmac?.[Platform.OS];

  useEffect(() => {
    if (skipUser) {
      client.init();
      return;
    }
    if (data) {
      const name = `${firstName} ${lastName}`;
      const userAttributes = {
        name,
        email: primaryEmail!,
        userId: externalId,
      };
      client.init(userAttributes, hmac);
    }
  }, [data]);

  return client;
}

export default function useIntercom({
  onFocus,
  skipUser = false,
}: { onFocus?: { event: string; metadata?: MetaData }; skipUser?: boolean } = {}) {
  const client = useIntercomClient({ skipUser });
  const focus = onFocus
    ? () => {
        client.logEvent(onFocus.event, onFocus.metadata || {});
      }
    : () => {};

  const callback = useCallback(focus, []);
  useFocusEffect(callback);
  return client;
}
